import { Controller } from "@hotwired/stimulus"

// Master audio panel for Clip Builder. Two slots: narration + background_music.
// Each slot accepts either an uploaded file or an AudioMix reference.
// Volume is debounced on input; PATCHed on `change`.
export default class extends Controller {
  static values = {
    uploadUrl: String,
    selectMixUrl: String,
    updateUrl: String,
    removeUrl: String,
    mixesUrl: String
  }

  static targets = ["slot", "fileInput", "volume", "volumeLabel", "mixPicker", "mixPickerLoading"]

  promptUpload(event) {
    const slot = event.currentTarget.dataset.slot
    this._fileInputForSlot(slot)?.click()
  }

  async handleUpload(event) {
    const input = event.target
    const slot = input.dataset.slot
    const file = input.files?.[0]
    if (!file) return

    const form = new FormData()
    form.append("slot", slot)
    form.append("file", file)

    try {
      const res = await fetch(this.uploadUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrf() },
        body: form
      })
      if (res.ok) location.reload()
    } catch (e) {
      // Silent: leave the slot empty so the user can retry
    } finally {
      input.value = ""
    }
  }

  async openMixPicker(event) {
    const slot = event.currentTarget.dataset.slot
    const picker = this._pickerForSlot(slot)
    if (!picker) return

    if (!picker.classList.contains("hidden")) {
      picker.classList.add("hidden")
      return
    }
    picker.classList.remove("hidden")

    try {
      const res = await fetch(this.mixesUrlValue, { headers: { Accept: "application/json" } })
      const data = await res.json()
      this._renderMixList(picker, slot, data.audio_mixes || [])
    } catch {
      picker.innerHTML = '<div class="p-2 text-xs text-red-500">Failed to load audio mixes</div>'
    }
  }

  _renderMixList(picker, slot, mixes) {
    if (mixes.length === 0) {
      picker.innerHTML = '<div class="p-2 text-xs text-gray-500">No ready audio mixes in this org yet.</div>'
      return
    }
    picker.innerHTML = mixes.map((m) => `
      <button type="button" data-action="click->clip-master-tracks#selectMix"
              data-slot="${slot}" data-mix-id="${m.id}"
              class="block w-full text-left px-2 py-1.5 text-xs hover:bg-indigo-50 border-b border-gray-100 last:border-b-0">
        <div class="font-medium text-gray-900 truncate">${this._escape(m.name)}</div>
        <div class="text-gray-500">${m.duration_seconds}s · ${this._escape(m.created_at)}</div>
      </button>
    `).join("")
  }

  async selectMix(event) {
    const btn = event.currentTarget
    const slot = btn.dataset.slot
    const mixId = btn.dataset.mixId

    try {
      const res = await fetch(this.selectMixUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrf(), "Content-Type": "application/json" },
        body: JSON.stringify({ slot, audio_mix_id: mixId })
      })
      if (res.ok) location.reload()
    } catch {
      // Silent
    }
  }

  previewVolume(event) {
    const slot = event.target.dataset.slot
    const pct = event.target.value
    const label = this.volumeLabelTargets.find((el) => el.dataset.slot === slot)
    if (label) label.textContent = `${pct}%`
  }

  async setVolume(event) {
    const slot = event.target.dataset.slot
    const volume = parseInt(event.target.value, 10) / 100
    try {
      await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: { "X-CSRF-Token": this._csrf(), "Content-Type": "application/json" },
        body: JSON.stringify({ slot, volume })
      })
    } catch {
      // Silent: visual already updated by previewVolume
    }
  }

  async removeTrack(event) {
    const slot = event.currentTarget.dataset.slot
    if (!confirm("Remove this track?")) return

    try {
      const res = await fetch(this.removeUrlValue, {
        method: "DELETE",
        headers: { "X-CSRF-Token": this._csrf(), "Content-Type": "application/json" },
        body: JSON.stringify({ slot })
      })
      if (res.ok) location.reload()
    } catch {
      // Silent
    }
  }

  _fileInputForSlot(slot) {
    return this.fileInputTargets.find((el) => el.dataset.slot === slot)
  }

  _pickerForSlot(slot) {
    return this.mixPickerTargets.find((el) => el.dataset.slot === slot)
  }

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _escape(str) {
    const div = document.createElement("div")
    div.textContent = str ?? ""
    return div.innerHTML
  }
}
